// TaskForm.js
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Divider,
  Paper,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  useTheme,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Close, Save, Add, Delete } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useAuth } from "../../hooks/useAuth";
import { useCollection } from "../../hooks/useCollection";
import {
  TASK_STATUS,
  TASK_STATUS_LABELS,
  TASK_PRIORITY,
  TASK_PRIORITY_LABELS,
  createTaskItem,
} from "../../utils/models/taskModel";
import {
  EVENT_TYPES,
  EVENT_TYPE_COLORS,
} from "../../utils/models/calendarModel";

const TaskForm = ({ task, onSave, onCancel }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { add, update } = useAuditFirestore("tasks");
  const { add: addEvent } = useAuditFirestore("calendarEvents");
  const { data: users } = useCollection("users");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    title: task?.title || "",
    description: task?.description || "",
    status: task?.status || TASK_STATUS.PENDING,
    priority: task?.priority || TASK_PRIORITY.MEDIUM,
    assignedTo: task?.id
      ? Array.isArray(task.assignedTo)
        ? task.assignedTo
        : task.assignedTo
          ? [task.assignedTo]
          : []
      : [],
    assignedToNames: task?.id
      ? Array.isArray(task.assignedToNames)
        ? task.assignedToNames
        : task.assignedToName
          ? [task.assignedToName]
          : []
      : [],
    assignedBy: task?.assignedBy || user.uid,
    assignedByName: task?.assignedByName || user.displayName || user.email,
    dueDate: task?.dueDate
      ? new Date(task.dueDate).toISOString().slice(0, 16)
      : "",
    dueTime: task?.dueDate
      ? new Date(task.dueDate).toTimeString().slice(0, 5)
      : "23:59",
    files: task?.files || [],
    addToCalendar: false,
  });

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "addToCalendar" ? checked : value,
    }));
    if (error) setError("");
  };

  const handleAssignedToChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      assignedTo: newValue.map((u) => u.id),
      assignedToNames: newValue.map((u) => u.displayName || u.email),
    }));
  };

  // Manejar archivos
  const handleFileChange = (index, field, value) => {
    const newFiles = [...formData.files];
    newFiles[index] = { ...newFiles[index], [field]: value };
    setFormData(prev => ({ ...prev, files: newFiles }));
  };

  const handleAddFile = () => {
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, { name: "", url: "" }]
    }));
  };

  const handleRemoveFile = (index) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, files: newFiles }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      setError("El título es obligatorio");
      return;
    }

    // Filtrar archivos vacíos
    const validFiles = formData.files.filter(file => file.name.trim() !== "" && file.url.trim() !== "");

    setLoading(true);
    setError("");
    try {
      const { addToCalendar, dueTime, ...taskData } = {
        ...formData,
        files: validFiles,
        dueDate: formData.dueDate
          ? new Date(formData.dueDate).toISOString()
          : null,
        updatedAt: new Date().toISOString(),
      };
      if (task?.id) {
        await update(task.id, taskData);
      } else {
        taskData.createdAt = new Date().toISOString();
        await add(taskData);
      }
      if (addToCalendar && formData.dueDate) {
        const dueDateObj = new Date(formData.dueDate);
        const endDate = new Date(dueDateObj.getTime() + 60 * 60 * 1000);
        await addEvent({
          title: `📋 ${formData.title}`,
          description: formData.description || "",
          eventType: EVENT_TYPES.TASK,
          startDate: dueDateObj.toISOString(),
          endDate: endDate.toISOString(),
          allDay: false,
          color: EVENT_TYPE_COLORS[EVENT_TYPES.TASK],
          createdBy: user.uid,
          createdByName: user.displayName || user.email,
          assignedTo: formData.assignedTo || [],
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };
  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}
          >
            {task?.id ? "Editar tarea" : "Nueva tarea"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
          >
            Gestiona tus tareas y asigna responsables
          </Typography>
        </Box>
        <IconButton
          onClick={onCancel}
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </Box>
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: { xs: 2, sm: 3 },
            borderRadius: "12px",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Typography sx={sectionTitleSx}>Información de la tarea</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2.5 },
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Título"
            name="title"
            value={formData.title}
            onChange={handleChange}
            sx={fieldSx}
            required
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Descripción"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                background: theme.palette.background.paper,
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
            }}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            >
              <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                Prioridad
              </InputLabel>
              <Select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                label="Prioridad"
                sx={{
                  borderRadius: "12px",
                  background: theme.palette.background.paper,
                  height: { xs: 40, sm: 44 },
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                {Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              size="small"
              sx={{ flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            >
              <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                Estado
              </InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Estado"
                sx={{
                  borderRadius: "12px",
                  background: theme.palette.background.paper,
                  height: { xs: 40, sm: 44 },
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                {Object.entries(TASK_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              multiple
              options={users || []}
              getOptionLabel={(option) =>
                option.displayName || option.email || ""
              }
              value={
                users?.filter((u) => formData.assignedTo?.includes(u.id)) || []
              }
              onChange={handleAssignedToChange}
              size="small"
              sx={{ flex: 1, minWidth: { xs: "100%", sm: "300px" } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asignar a"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: theme.palette.background.paper,
                      minHeight: { xs: 40, sm: 44 },
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Fecha de vencimiento"
                name="dueDate"
                type="datetime-local"
                value={formData.dueDate}
                onChange={handleChange}
                sx={{ ...fieldSx, flex: 2 }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </Box>

          {/* ARCHIVOS */}
          <Typography sx={sectionTitleSx}>Archivos</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
            {formData.files.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: "center",
                  width: "100%",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <TextField
                  size="small"
                  label="Nombre del archivo"
                  value={file.name}
                  onChange={(e) => handleFileChange(index, "name", e.target.value)}
                  sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
                  inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
                />
                <TextField
                  size="small"
                  label="Enlace (URL)"
                  value={file.url}
                  onChange={(e) => handleFileChange(index, "url", e.target.value)}
                  sx={{ ...fieldSx, flex: 2, minWidth: { xs: "100%", sm: "300px" } }}
                  inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
                />
                <IconButton
                  onClick={() => handleRemoveFile(index)}
                  size="small"
                  sx={{
                    width: { xs: 40, sm: 44 },
                    height: { xs: 40, sm: 44 },
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: theme.palette.error.main,
                      color: "white",
                    },
                  }}
                >
                  <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Box>
            ))}
            
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddFile}
              sx={{
                borderRadius: "12px",
                height: { xs: 40, sm: 44 },
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                alignSelf: "flex-start",
                mt: 1,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(0, 175, 163, 0.1)' 
                    : 'rgba(0, 95, 101, 0.05)',
                },
              }}
            >
              Añadir archivo
            </Button>
          </Box>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="addToCalendar"
                  checked={formData.addToCalendar}
                  onChange={handleChange}
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                  Añadir al calendario como evento
                </Typography>
              }
            />
          </Box>
          {task?.id && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
              >
                Creada por: {formData.assignedByName}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              borderRadius: "12px",
              px: { xs: 2, sm: 3 },
              height: { xs: 40, sm: 44 },
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
            fullWidth={{ xs: true, sm: false }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<Save />}
            sx={{
              borderRadius: "12px",
              px: { xs: 2, sm: 3 },
              height: { xs: 40, sm: 44 },
              boxShadow: "none",
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
            fullWidth={{ xs: true, sm: false }}
          >
            {loading ? "Guardando..." : task?.id ? "Actualizar" : "Crear tarea"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default TaskForm;